import React, { useState } from 'react'
import { AuthGetResponse, NumbersACH, AccountBase, Institution } from 'plaid'
import { Spinner, SpinnerSize } from '@fluentui/react'
import api from '../api-client'
import { usePlaid } from '../hooks'

const plaidErrorToString = (err: unknown): string => {
  if (typeof err === 'string') {
    return err
  }

  if (!err || typeof err !== 'object') {
    return 'Please retry.'
  }

  const record = err as {
    error_message?: unknown
    display_message?: unknown
    error?: unknown
  }

  if (typeof record.error_message === 'string' && record.error_message) {
    return typeof record.display_message === 'string' && record.display_message
      ? record.display_message
      : record.error_message
  }

  if (typeof record.error === 'string' && record.error) {
    return record.error
  }

  return 'Please retry.'
}

type PlaidAuthProps = {
  prompt: string
  loanApplicationId: string
  onAuthSelect: (params: {
    ach: NumbersACH,
    account: AccountBase,
    institution: Institution
  }) => void
  onCancel?: () => void
}

type OEAuthGetResponse = AuthGetResponse & { institution: Institution }

export const PlaidAuthSelect = ({
  onAuthSelect,
  prompt,
  loanApplicationId,
  onCancel,
}: PlaidAuthProps) => {
  const [agr, setAuthGetResponse] = useState<OEAuthGetResponse>(null)
  const [plaidError, setPlaidError] = useState<unknown>()
  const [loadingAccounts, setLoadingAccounts] = useState(false)

  const { ready, open } = usePlaid({
    onSuccess: (public_token) => {
      const query = new URLSearchParams({ public_token })
      if (loanApplicationId) {
        query.set('loanApplicationId', loanApplicationId)
      }
      setLoadingAccounts(true)
      api.get(`/plaid/auth?${query.toString()}`)
        .then(setAuthGetResponse)
        .catch(setPlaidError)
        .finally(() => setLoadingAccounts(false))
    },
    onError(err) {
      console.error(`plaid error`, err)
      setPlaidError(err)
    },
    onExit(reason) {
      if (!reason) {
        return
      }

      console.error('plaid exit', reason)
      setPlaidError(reason)
    }
  })

  return <div>
    {loadingAccounts ? (
      <Spinner size={SpinnerSize.large} label="Loading your bank accounts" />
    ) : agr ? <>
      <p>{prompt}</p>
      <div className="list-group">
        {agr.accounts.map(a => <div
          key={a.account_id}
          className="list-group-item list-group-item-action pointer"
          onClick={() => {
            const ach = agr.numbers.ach.find(ach => ach.account_id === a.account_id)
            onAuthSelect({ ach, account: a, institution: agr.institution })
          }}
        >
          <div>{a.name} ({a.mask})</div>
          <div style={{ color: '#aaa', fontSize: '0.8em' }}>{a.subtype}</div>
        </div>)}
      </div>
    </> : <>
      {plaidError ? <div>
        <div className="alert alert-danger">
          <p>There was an error linking your autopay account. Please retry.</p>
          <p><b>Error:</b>&nbsp;{plaidErrorToString(plaidError)}</p>
        </div>
        <div className="buttons" style={{ textAlign: 'left' }}>
          <button type="button" className="btn btn-primary" onClick={() => { setPlaidError(undefined); open() }}>Retry</button>
        </div>
      </div> : <div className="buttons" style={{ textAlign: 'left' }}>
        <button type="button" disabled={!ready} className="btn btn-primary" onClick={() => open()}>Setup Auto-payment</button>
        {onCancel ? (
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>}
    </>}
  </div>
}

export default PlaidAuthSelect